import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { Dashboard } from '@/pages/Dashboard';
import { Settings } from '@/pages/Settings';
import { PeopleRegister } from '@/pages/People/Register';
import { PersonEditForm } from '@/pages/People/EditForm';
import { PersonDetail } from '@/pages/People/Detail';
import { PendingAccounts } from '@/pages/People/PendingAccounts';
import { AccountsRegister } from '@/pages/Accounts/Register';
import { AccountEditForm } from '@/pages/Accounts/EditForm';
import { AccountDetail } from '@/pages/Accounts/Detail';
import { AssetsRegister } from '@/pages/Assets/Register';
import { AssetEditForm } from '@/pages/Assets/EditForm';
import { AssetDetail } from '@/pages/Assets/Detail';
import { IntuneOrphans } from '@/pages/Assets/IntuneOrphans';
import { DeviceSecurity } from '@/pages/Assets/DeviceSecurity';
import { SoftwareRegister } from '@/pages/Software/Register';
import { SoftwareEditForm } from '@/pages/Software/EditForm';
import { SoftwareDetail } from '@/pages/Software/Detail';
import { StockPage } from '@/pages/Stock';
import { StockQuotesPage } from '@/pages/Stock/Quotes';
import { HardwareRequestsPage } from '@/pages/HardwareRequests';
import { HardwareRequestDetail } from '@/pages/HardwareRequests/Detail';
import { ServicesPage } from '@/pages/Services';
import { TierMapPage } from '@/pages/Services/TierMap';
import { LocationsPage } from '@/pages/Locations';
import { LocationEditForm } from '@/pages/Locations/EditForm';
import { LocationHierarchyPage } from '@/pages/Locations/Hierarchy';
import { DepartmentBuilder } from '@/pages/Onboarding/DepartmentBuilder';
import { PositionTitleAllocator } from '@/pages/Onboarding/PositionTitleAllocator';
import { SoftwareAllocation } from '@/pages/Onboarding/SoftwareAllocation';
import { AccessGroupAllocation } from '@/pages/Onboarding/AccessGroupAllocation';
import { HardwareAllocation } from '@/pages/Onboarding/HardwareAllocation';
import { NewStarters } from '@/pages/Onboarding/NewStarters';
import { ContractsRegister } from '@/pages/Contracts/Register';
import { ContractEditForm } from '@/pages/Contracts/EditForm';
import { ContractDetail } from '@/pages/Contracts/Detail';
import { ContractGlDefaults } from '@/pages/Contracts/GlDefaults';
import { FinanceExport } from '@/pages/Finance/Export';
import { OnPassCreate } from '@/pages/OnPass/Create';
import { OnPassDashboard } from '@/pages/OnPass/Dashboard';
import { OnPassRetrieve } from '@/pages/OnPass/Retrieve';
import { AdminPage } from '@/pages/Admin';
import { BranchRelationshipsPage } from '@/pages/BranchRelationships';
import { OnsiteLiveAdminPage } from '@/pages/OnsiteLiveAdmin';
import { OnsiteLiveTeamsPage } from '@/pages/OnsiteLiveTeams';
import { MenuConfigPage } from '@/pages/MenuConfig';
import { DynamicRuleSearchPage } from '@/pages/DynamicRules';
import { DynamicRuleBuilderPage } from '@/pages/DynamicRules/Builder';
import { AccessReviews } from '@/pages/AccessReviews';
import { OffboardingStatus } from '@/pages/Offboarding/OffboardingStatus';
import { OffboardingJobs } from '@/pages/Offboarding/OffboardingJobs';
import { RequireRole } from '@/auth/RequireRole';
import { useAuth } from '@/auth/useAuth';
import type { OscarRole } from '@/types/oscar';

// Role groups for route gating, mirroring the server-side AuthHelper:
//   READ   = any IT role can view the registers (AuthHelper.CanRead)
//   MUTATE = admin/helpdesk can create/edit (AuthHelper.CanMutate)
//   FINANCE = finance/admin for the CSV export (AuthHelper.CanExportFinance)
const READ: OscarRole[] = ['oscar-admin', 'oscar-helpdesk', 'oscar-finance', 'oscar-reviewer'];
const MUTATE: OscarRole[] = ['oscar-admin', 'oscar-helpdesk'];
const FINANCE: OscarRole[] = ['oscar-finance', 'oscar-admin'];

/**
 * Routing table for Oscar.
 *
 * Auth flow (mirrors the cab / manage tenants):
 *   1. SWA `responseOverrides.401` redirects anonymous requests for any
 *      protected route straight to `/.auth/login/aad`.
 *   2. As defence-in-depth, if `/.auth/me` returns no principal while the
 *      SPA is already loaded (e.g. session expired in-tab), we trigger the
 *      same AAD redirect from the client.
 *   3. Role-gated behaviour lives inside each feature page using
 *      `useAuth()` flags (isAdmin / isHelpdesk / …) and `<RequireRole>` —
 *      never at the route level. Server-side checks in /api/* are
 *      authoritative.
 *
 * Feature routes:
 *   Phase 1 features (People, Accounts, Devices, Software, SIMs, Contracts,
 *   Finance export) are added here as they land per oscar/docs/Next-Steps.md.
 *   Stage 1 ships only the Dashboard and Settings placeholders.
 */
// How recently we may have already bounced to sign-in. If we come back still
// unauthenticated inside this window, we treat it as a redirect loop and stop
// (show a manual sign-in link) rather than bouncing again.
const AUTH_REDIRECT_KEY = 'oscar.authRedirectAt';
const AUTH_REDIRECT_LOOP_MS = 15000;

// The deep link we were on before sign-in (e.g. an OnPass /s/<token> share
// link). We restore it after login ourselves because the SWA does not reliably
// honour post_login_redirect_uri — it tends to drop the user on "/".
const RETURN_TO_KEY = 'oscar.returnTo';

const currentPath = () =>
  window.location.pathname + window.location.search + window.location.hash;

const loginUrl = () =>
  `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(currentPath() || '/')}`;

export function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  const [loopBlocked, setLoopBlocked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      // Clean session: clear the guard so a later expiry redirects normally.
      sessionStorage.removeItem(AUTH_REDIRECT_KEY);
      return;
    }
    if (loading) return;

    const last = Number(sessionStorage.getItem(AUTH_REDIRECT_KEY) || 0);
    if (last && Date.now() - last < AUTH_REDIRECT_LOOP_MS) {
      // We already bounced to sign-in very recently and we're STILL not
      // authenticated — don't loop. Surface a manual sign-in instead.
      setLoopBlocked(true);
      return;
    }
    // Remember the deep link (e.g. a /s/<token> share link) so we can restore
    // it after login regardless of whether the SWA honours the redirect URI.
    const dest = currentPath();
    if (dest && dest !== '/') sessionStorage.setItem(RETURN_TO_KEY, dest);
    sessionStorage.setItem(AUTH_REDIRECT_KEY, String(Date.now()));
    window.location.replace(loginUrl());
  }, [loading, isAuthenticated]);

  // Once authenticated, if the SWA dropped us somewhere other than the deep
  // link we saved before sign-in, restore it (client-side, no reload).
  useEffect(() => {
    if (!isAuthenticated) return;
    const dest = sessionStorage.getItem(RETURN_TO_KEY);
    if (!dest) return;
    sessionStorage.removeItem(RETURN_TO_KEY);
    if (dest !== currentPath()) navigate(dest, { replace: true });
  }, [isAuthenticated, navigate]);

  if (loading || !isAuthenticated) {
    if (loopBlocked) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-600">
          <p>We couldn&rsquo;t complete sign-in automatically.</p>
          <a
            href={loginUrl()}
            onClick={() => sessionStorage.removeItem(AUTH_REDIRECT_KEY)}
            className="rounded bg-orange px-4 py-2 font-semibold text-white hover:opacity-90"
          >
            Sign in
          </a>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Redirecting to sign-in…
      </div>
    );
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/people" element={<RequireRole anyOf={READ}><PeopleRegister /></RequireRole>} />
        <Route path="/people/new" element={<RequireRole anyOf={MUTATE}><PersonEditForm /></RequireRole>} />
        <Route path="/people/pending" element={<RequireRole anyOf={MUTATE}><PendingAccounts /></RequireRole>} />
        <Route path="/people/:id" element={<RequireRole anyOf={READ}><PersonDetail /></RequireRole>} />
        <Route path="/people/:id/edit" element={<RequireRole anyOf={MUTATE}><PersonEditForm /></RequireRole>} />
        <Route path="/accounts" element={<RequireRole anyOf={READ}><AccountsRegister /></RequireRole>} />
        <Route path="/accounts/new" element={<RequireRole anyOf={MUTATE}><AccountEditForm /></RequireRole>} />
        <Route path="/accounts/:id" element={<RequireRole anyOf={READ}><AccountDetail /></RequireRole>} />
        <Route path="/accounts/:id/edit" element={<RequireRole anyOf={MUTATE}><AccountEditForm /></RequireRole>} />
        {/* System Access Reviews (SAR) — placeholder nav slot; full feature is a later phase. */}
        <Route path="/access-reviews" element={<RequireRole anyOf={READ}><AccessReviews /></RequireRole>} />
        <Route path="/offboarding" element={<RequireRole anyOf={READ}><OffboardingStatus /></RequireRole>} />
        <Route path="/offboarding/jobs" element={<RequireRole anyOf={READ}><OffboardingJobs /></RequireRole>} />
        <Route path="/assets" element={<RequireRole anyOf={READ}><AssetsRegister /></RequireRole>} />
        <Route path="/assets/intune-orphans" element={<RequireRole anyOf={READ}><IntuneOrphans /></RequireRole>} />
        <Route path="/assets/device-security" element={<RequireRole anyOf={READ}><DeviceSecurity /></RequireRole>} />
        <Route path="/assets/new" element={<RequireRole anyOf={MUTATE}><AssetEditForm /></RequireRole>} />
        <Route path="/assets/:id" element={<RequireRole anyOf={READ}><AssetDetail /></RequireRole>} />
        <Route path="/assets/:id/edit" element={<RequireRole anyOf={MUTATE}><AssetEditForm /></RequireRole>} />
        <Route path="/software" element={<RequireRole anyOf={READ}><SoftwareRegister /></RequireRole>} />
        <Route path="/software/new" element={<RequireRole anyOf={MUTATE}><SoftwareEditForm /></RequireRole>} />
        <Route path="/software/:id" element={<RequireRole anyOf={READ}><SoftwareDetail /></RequireRole>} />
        <Route path="/software/:id/edit" element={<RequireRole anyOf={MUTATE}><SoftwareEditForm /></RequireRole>} />
        <Route path="/stock" element={<RequireRole anyOf={READ}><StockPage /></RequireRole>} />
        {/* ⚠ ONE route, not /stock/quotes/:id as well — the detail renders in
            place. Keeps the URL surface to the single route that was agreed. */}
        <Route path="/stock/quotes" element={<RequireRole anyOf={READ}><StockQuotesPage /></RequireRole>} />
        {/* Hardware request registry (design doc Phase 1). READ ONLY — no approval yet.
            Gated on MUTATE, not READ: a request carries the requester's delivery address
            and possibly a personal mobile number, so finance and reviewer are kept out.
            The API enforces the same narrowing; this hides the link. */}
        <Route path="/hardware-requests" element={<RequireRole anyOf={MUTATE}><HardwareRequestsPage /></RequireRole>} />
        <Route path="/hardware-requests/:id" element={<RequireRole anyOf={MUTATE}><HardwareRequestDetail /></RequireRole>} />
        <Route path="/services" element={<RequireRole anyOf={READ}><ServicesPage /></RequireRole>} />
        <Route path="/services/tiers" element={<RequireRole anyOf={READ}><TierMapPage /></RequireRole>} />
        <Route path="/locations" element={<RequireRole anyOf={READ}><LocationsPage /></RequireRole>} />
        <Route path="/locations/hierarchy" element={<RequireRole anyOf={['oscar-admin']}><LocationHierarchyPage /></RequireRole>} />
        <Route path="/locations/new" element={<RequireRole anyOf={['oscar-admin']}><LocationEditForm /></RequireRole>} />
        <Route path="/locations/:id/edit" element={<RequireRole anyOf={['oscar-admin']}><LocationEditForm /></RequireRole>} />
        {/* Onboarding is an admin function (Admin > Onboarding in the nav). */}
        <Route path="/onboarding/departments" element={<RequireRole anyOf={['oscar-admin']}><DepartmentBuilder /></RequireRole>} />
        <Route path="/onboarding/position-titles" element={<RequireRole anyOf={['oscar-admin']}><PositionTitleAllocator /></RequireRole>} />
        <Route path="/onboarding/software" element={<RequireRole anyOf={['oscar-admin']}><SoftwareAllocation /></RequireRole>} />
        {/* The OnsiteLive and Baseplan groups each department's people get (team
            walk-through 26/10/2026). Admin, like Software Allocation; the menu
            row is OnsiteCore-data-oscar-menu-access-groups.sql. */}
        <Route path="/onboarding/access-groups" element={<RequireRole anyOf={['oscar-admin']}><AccessGroupAllocation /></RequireRole>} />
        {/* Phase 11. READ is open to every Oscar role on purpose - helpdesk
            fields "why can't I pick a laptop?" and the answer is on this
            screen. Allocating is admin, enforced server-side. */}
        <Route path="/onboarding/hardware" element={<RequireRole anyOf={READ}><HardwareAllocation /></RequireRole>} />
        {/* The Hardware Request device/accessory catalogue, moved here from
            manage's /settings/hardware on 30/09/2026 (design §13). READ is open
            to every Oscar role on purpose — helpdesk needs to see what the form
            offers when somebody asks why a device is missing — and the page
            hides its own edit controls from a non-admin. Saves are admin-only
            server-side, which is the guarantee. */}
        {/* One page now (01/11/2026): the catalogue is the second view on Stock. Kept as a redirect so the menu entry and bookmarks still land. */}
        <Route path="/hardware-catalogue" element={<Navigate to="/stock?view=catalogue" replace />} />
        {/* PR-36 ELMO new starters. The queue is readable by admin+helpdesk (the
            two roles that can act on it); approving reuses the People edit form,
            which posts to the approve endpoint instead of creating directly. */}
        <Route path="/onboarding/new-starters" element={<RequireRole anyOf={MUTATE}><NewStarters /></RequireRole>} />
        <Route path="/onboarding/new-starters/:elmoUserId/approve" element={<RequireRole anyOf={MUTATE}><PersonEditForm /></RequireRole>} />
        <Route path="/contracts" element={<RequireRole anyOf={READ}><ContractsRegister /></RequireRole>} />
        <Route path="/contracts/new" element={<RequireRole anyOf={MUTATE}><ContractEditForm /></RequireRole>} />
        <Route path="/contracts/:id" element={<RequireRole anyOf={READ}><ContractDetail /></RequireRole>} />
        <Route path="/contracts/:id/edit" element={<RequireRole anyOf={MUTATE}><ContractEditForm /></RequireRole>} />
        <Route path="/contracts/gl-defaults" element={<RequireRole anyOf={['oscar-admin']}><ContractGlDefaults /></RequireRole>} />
        <Route path="/finance/export" element={<RequireRole anyOf={FINANCE}><FinanceExport /></RequireRole>} />
        {/* OnPass (PR-23). Create/dashboard gate admin+helpdesk inside the page;
            the retrieve route /s/:token is reachable by any authenticated user
            (no role) — see oscar/docs/OnPass-Plan.md. No staticwebapp.config.json
            change: everything stays authenticated. */}
        <Route path="/onpass" element={<OnPassCreate />} />
        <Route path="/onpass/active" element={<OnPassDashboard />} />
        <Route path="/s/:token" element={<OnPassRetrieve />} />
        {/* IT Support dynamic-rule search (admin + helpdesk, enforced server-side too). */}
        <Route path="/dynamic-rules" element={<RequireRole anyOf={MUTATE}><DynamicRuleSearchPage /></RequireRole>} />
        <Route path="/dynamic-rules/builder" element={<RequireRole anyOf={MUTATE}><DynamicRuleBuilderPage /></RequireRole>} />
        <Route path="/admin" element={<RequireRole anyOf={['oscar-admin']}><AdminPage /></RequireRole>} />
        <Route path="/settings" element={<Settings />} />
        {/* Branch relationships (migration 128). Admin maintains it; helpdesk reads it
            to answer "why can't I see branch X?". Writes are admin-only server-side. */}
        <Route path="/settings/branch-relationships" element={<RequireRole anyOf={MUTATE}><BranchRelationshipsPage /></RequireRole>} />
        {/* OnsiteLive users (migrations 141/142) - replaces OnBudget's Onsite Admin. Helpdesk reads;
            admin writes, and only after the cutover (the API refuses before it). */}
        <Route path="/settings/onsitelive" element={<RequireRole anyOf={MUTATE}><OnsiteLiveAdminPage /></RequireRole>} />
        <Route path="/settings/onsitelive-teams" element={<RequireRole anyOf={MUTATE}><OnsiteLiveTeamsPage /></RequireRole>} />
        {/* The sidebar's own editor (migration 131). Linked from the sidebar itself, app-rendered for admins. */}
        <Route path="/settings/menu" element={<RequireRole anyOf={['oscar-admin']}><MenuConfigPage /></RequireRole>} />
        {/* Phase 1 feature routes are added here as they land in src/pages/. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
